/**
 * A Premium Hiring lead, exactly as `getLeads` returns it (lead.graphqls). There is no notes or
 * UTM field on the record: `source` is the only attribution the backend stores.
 */
export interface Lead {
  _id: string;
  company?: string | null;
  contactPerson?: string | null;
  city?: string | null;
  workEmail?: string | null;
  phone?: string | null;
  roles?: (string | null)[] | null;
  approxOpenings?: number | null;
  timeline?: string | null;
  status?: string | null;
  employerId?: string | null;
  source?: string | null;
  createdAt?: string | null;
}

/** The lead's display name: the company, which is what an HR calls a lead by. */
export const leadName = (l: Pick<Lead, 'company'>): string => l.company?.trim() || 'Unknown company';

/** Two letters for the identity avatar, from the company's first two words. */
export const leadInitials = (l: Pick<Lead, 'company'>): string => {
  const words = (l.company ?? '').trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? `${words[0][0]}${words[1][0]}` : (words[0] ?? '').slice(0, 2);
  return letters.toUpperCase() || 'L';
};

/** The roles as listed, without the blanks a form can submit. */
export const leadRoles = (l: Pick<Lead, 'roles'>): string[] =>
  (l.roles ?? []).map((r) => (r ?? '').trim()).filter(Boolean);

/** "About 3", or null when the form left it empty or zero. */
export const openingsLabel = (n: number | null | undefined): string | null =>
  typeof n === 'number' && n > 0 ? `About ${n.toLocaleString('en-IN')}` : null;

/**
 * One lead out of the list, by id.
 *
 * `getLeads` is the only read the schema offers (no `getLeadById`), and it returns every lead in
 * one unpaginated array, so the workspace picks its record out of that rather than asking the
 * backend for a new query.
 */
export const findLead = (leads: Lead[] | null | undefined, id: string | undefined): Lead | null =>
  (id && (leads ?? []).find((l) => l?._id === id)) || null;
